/**
 * AppStoreBanner — platform-aware app promo for WEB visitors (Aug 2026).
 *  - iPhone/iPad: "Get the app" → App Store listing (Safari also shows
 *    Apple's native Smart App Banner via the apple-itunes-app meta tag).
 *  - Android: "Coming soon to Google Play" + Notify-me email capture
 *    (feeds the expansion_interest waitlist, source=android-waitlist).
 * Never renders inside the native app, on desktop, or for 30 days after
 * dismissal.
 */
import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { X } from "@phosphor-icons/react";
import { APP_STORE_URL, PLAY_STORE_URL, ANDROID_LIVE } from "../lib/appStores";

const DISMISS_KEY = "tt_appstore_banner_dismissed_at";
const DISMISS_DAYS = 30;

const platformForBanner = () => {
  if (Capacitor.isNativePlatform()) return null;
  const ua = navigator.userAgent || "";
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/i.test(ua) || iPadOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return null;
};

export const AppStoreBanner = () => {
  const [hidden, setHidden] = useState(() => {
    try {
      const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
      return t && Date.now() - t < DISMISS_DAYS * 864e5;
    } catch { return false; }
  });

  const platform = platformForBanner();
  if (hidden || !platform) return null;
  // Android app is submitted but still in Google review — don't send
  // Android visitors to a listing they can't install from yet.
  if (platform === "android" && !ANDROID_LIVE) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setHidden(true);
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[60] px-3 pb-3"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      data-testid="appstore-banner"
    >
      <div className="mx-auto max-w-md rounded-2xl border border-border bg-card shadow-xl flex items-center gap-3 p-3">
        <img
          src="/icon-192.png"
          alt="TruckTrackr app"
          className="h-11 w-11 rounded-xl flex-none"
          onError={(e) => { e.currentTarget.src = "/icon-512.png"; }}
        />
        {platform === "ios" ? (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">TruckTrackr for iPhone</div>
              <div className="text-xs text-muted-foreground leading-tight mt-0.5">
                Truck alerts, live map & more — free
              </div>
            </div>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-none h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center hover:opacity-90 transition-opacity"
              data-testid="appstore-banner-get"
            >
              Get
            </a>
          </>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">TruckTrackr for Android</div>
              <div className="text-xs text-muted-foreground leading-tight mt-0.5">
                Truck alerts, live map & more — free
              </div>
            </div>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-none h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center hover:opacity-90 transition-opacity"
              data-testid="playstore-banner-get"
            >
              Get
            </a>
          </>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-none h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground transition-colors"
          data-testid="appstore-banner-dismiss"
        >
          <X weight="bold" className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default AppStoreBanner;

